import React, { useMemo, useState } from 'react';
import { PlusCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useHousesStore } from '../../store/useStore.js';
import HouseCard from './HouseCard.jsx';

const sortOptions = [
  { value: 'newest', label: 'Newest first' },
  { value: 'price-asc', label: 'Price: Low to high' },
  { value: 'price-desc', label: 'Price: High to low' },
];

const scoreFilters = [
  { value: 'all', label: 'All scores' },
  { value: 'green', label: 'Green only' },
  { value: 'yellow', label: 'Yellow only' },
  { value: 'red', label: 'Red only' },
];

export default function HouseGrid({ onSelectHouse }) {
  const navigate = useNavigate();
  const { houses, isLoading } = useHousesStore();
  const [sortBy, setSortBy] = useState('newest');
  const [scoreFilter, setScoreFilter] = useState('all');

  const filtered = useMemo(() => {
    let result = [...houses];

    if (scoreFilter !== 'all') {
      result = result.filter((house) => house.score === scoreFilter);
    }

    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-desc':
        result.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'newest':
      default:
        result.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
        break;
    }

    return result;
  }, [houses, scoreFilter, sortBy]);

  return (
    <section className="bg-white rounded-xl shadow-lg border border-indigo-100 p-6 space-y-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Saved Houses</h2>
          <p className="text-sm text-gray-500">Track every home you and your household are considering.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/houses/analyze')}
          className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700"
        >
          <PlusCircle className="w-4 h-4" />
          Analyze New House
        </button>
      </header>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex gap-3">
          <label className="text-sm text-gray-500 flex items-center gap-2">
            Sort by
            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-500 flex items-center gap-2">
            Score
            <select
              value={scoreFilter}
              onChange={(event) => setScoreFilter(event.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {scoreFilters.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading your saved houses…</span>
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <div className="border border-dashed border-indigo-200 rounded-xl p-8 text-center text-sm text-indigo-500 bg-indigo-50/40">
          No houses saved yet. Use the analyzer to add your first property!
        </div>
      )}

      {filtered.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((house) => (
            <HouseCard key={house.id} house={house} onSelect={onSelectHouse} />
          ))}
        </div>
      )}
    </section>
  );
}
